import React, { useState, useEffect, useRef } from "react";
import "./FeedbackRating.css";
import {
  FeedbackRating as FeedbackRatingType,
  IndividualType,
  User,
  Employee,
} from "winslow-core";
import {
  apiAIUpdateFeedbackRating,
  apiAIDeleteFeedbackRating,
} from "../../services/AIService";
import { selectUser } from "../../redux/features/appData";
import { useSelector } from "react-redux";
import { CircleXIcon } from "lucide-react";
import { Button } from "../../ds2/components";

type FeedbackRatingProps = {
  feedbackRating?: FeedbackRatingType;
  className?: string;
  onRatingUpdate?: (rating: number) => void;
  onFeedbackSubmit?: (feedback: string) => void;
  onRatingDelete?: () => void;
  messageId: number;
  alwaysVisible?: boolean;
};

export const FeedbackRating = ({
  feedbackRating,
  className = "",
  onRatingUpdate,
  onFeedbackSubmit,
  onRatingDelete,
  messageId,
  alwaysVisible = false,
}: FeedbackRatingProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localFeedbackRating, setLocalFeedbackRating] = useState<
    FeedbackRatingType | undefined
  >(feedbackRating);
  const [hasNewRating, setHasNewRating] = useState(false);
  const [initialFeedbackText, setInitialFeedbackText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const user = useSelector(selectUser);

  // Check if input is dirty (has changed from initial value)
  const isInputDirty = feedbackText !== initialFeedbackText;

  // Update local state when prop changes
  React.useEffect(() => {
    setLocalFeedbackRating(feedbackRating);
    // Reset new rating flag when feedbackRating prop changes
    setHasNewRating(false);
  }, [feedbackRating]);

  // Auto-focus textarea when input overlay opens
  useEffect(() => {
    if (showFeedbackInput && textareaRef.current) {
      textareaRef.current.focus();
      // Place cursor at the end of the text
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [showFeedbackInput]);

  // Handle click away to close feedback input
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFeedbackInput) {
        const target = event.target as Element;
        if (!target.closest(".feedback-rating-input-overlay")) {
          setShowFeedbackInput(false);
          setFeedbackText("");
          setInitialFeedbackText("");
          setHasNewRating(false);
        }
      }
    };

    if (showFeedbackInput) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFeedbackInput]);

  const handleStarClick = async (rating: number) => {
    try {
      // Mark that a new rating has been selected
      setHasNewRating(true);

      // Update the rating via API
      if (onRatingUpdate) {
        onRatingUpdate(rating);
      } else {
        const response = await apiAIUpdateFeedbackRating({
          messageId: messageId,
          individualId: user!.id,
          individualType: IndividualType.USER,
          feedback: localFeedbackRating?.feedback || null, // Preserve existing feedback
          rating,
        });

        // Update local state with the response
        if (response) {
          setLocalFeedbackRating(response);
        }
      }

      // Prepopulate with existing feedback and show input
      const existingFeedback = localFeedbackRating?.feedback || "";
      setFeedbackText(existingFeedback);
      setInitialFeedbackText(existingFeedback);
      setShowFeedbackInput(true);
    } catch (error) {
      console.error("Error updating rating:", error);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!isInputDirty || !feedbackText.trim()) return;

    setIsSubmitting(true);
    try {
      if (onFeedbackSubmit) {
        onFeedbackSubmit(feedbackText);
      } else {
        const response = await apiAIUpdateFeedbackRating({
          messageId: messageId,
          individualId: user!.id,
          individualType: IndividualType.USER,
          feedback: feedbackText,
          rating: localFeedbackRating?.rating || 0,
        });

        // Update local state with the response
        if (response) {
          setLocalFeedbackRating(response);
        }
      }

      setShowFeedbackInput(false);
      setFeedbackText("");
      setInitialFeedbackText("");
      setHasNewRating(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    const stars = [];
    const currentRating = localFeedbackRating?.rating || 0;

    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= currentRating;
      const isHoveredStar = (isHovered || alwaysVisible) && i <= currentRating;

      stars.push(
        <span
          key={i}
          className={`feedback-rating-star ${isFilled ? "filled" : "empty"} ${
            isHoveredStar ? "hovered" : ""
          }`}
          onClick={() => handleStarClick(i)}>
          ★
        </span>
      );
    }

    if (
      (isHovered || alwaysVisible) &&
      localFeedbackRating?.rating &&
      localFeedbackRating.rating > 0
    ) {
      stars.push(
        <span
          className="delete-feedback-rating-icon"
          title="Delete rating"
          aria-label="Delete rating"
          onClick={() => handleDeleteRating()}>
          <CircleXIcon height={14} width={14} color="#dc2626" />
        </span>
      );
    }
    return stars;
  };

  const handleDeleteRating = async () => {
    try {
      console.log("Deleting feedback rating for messageId:", messageId);
      await apiAIDeleteFeedbackRating(messageId);
      setLocalFeedbackRating(undefined);
      setShowFeedbackInput(false);
      setFeedbackText("");
      setHasNewRating(false);
      if (onRatingDelete) {
        onRatingDelete();
      }
    } catch (err) {
      console.error("Error deleting feedback rating:", err);
    }
  };

  const getIndividualDisplayName = (): string => {
    if (!localFeedbackRating) return "Unknown";

    if (localFeedbackRating.individualType === IndividualType.USER) {
      const u = localFeedbackRating.user as User | null | undefined;
      if (!u) return "Unknown User";
      if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
      if (u.firstName) return u.firstName;
      if (u.username) return u.username;
      return u.email || "Unknown User";
    }

    if (localFeedbackRating.individualType === IndividualType.EMPLOYEE) {
      const e = localFeedbackRating.employee as Employee | null | undefined;
      if (!e) return "Unknown Employee";
      if (e.displayName) return e.displayName;
      return e.email || "Unknown Employee";
    }

    return "Unknown";
  };

  const getIndividualAvatarURL = (): string | null => {
    if (!localFeedbackRating) return null;

    if (localFeedbackRating.individualType === IndividualType.USER) {
      const u = localFeedbackRating.user as User | null | undefined;
      return u?.avatarURL ?? null;
    }

    if (localFeedbackRating.individualType === IndividualType.EMPLOYEE) {
      const e = localFeedbackRating.employee as Employee | null | undefined;
      return e?.avatar ?? null;
    }

    return null;
  };

  return (
    <div
      className={`feedback-rating ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <div className="feedback-rating-stars">{renderStars()}</div>

      {/* Tooltip - show when hovering over existing rating with feedback */}
      {isHovered &&
        !showFeedbackInput &&
        !hasNewRating &&
        localFeedbackRating?.rating &&
        localFeedbackRating.rating > 0 &&
        localFeedbackRating.feedback &&
        (localFeedbackRating?.user || localFeedbackRating?.employee) && (
          <div className="feedback-rating-tooltip">
            <div className="feedback-rating-tooltip-header">
              {getIndividualAvatarURL() && (
                <div className="feedback-rating-tooltip-avatar">
                  <img src={getIndividualAvatarURL()!} alt="Avatar" />
                </div>
              )}
              <div className="feedback-rating-tooltip-user">
                {getIndividualDisplayName()}
              </div>
            </div>
            <div className="feedback-rating-tooltip-feedback">
              {localFeedbackRating.feedback}
            </div>
          </div>
        )}

      {/* Feedback input overlay */}
      {showFeedbackInput && (
        <div
          className="feedback-rating-input-overlay"
          onClick={(e) => e.stopPropagation()}>
          <div className="agent-chat-input-row">
            <textarea
              ref={textareaRef}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Share your thoughts..."
              rows={1}
              maxLength={500}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                const newHeight = Math.min(target.scrollHeight, 120);
                target.style.height = `${newHeight}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (feedbackText.trim() && !isSubmitting) {
                    handleFeedbackSubmit();
                  }
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setShowFeedbackInput(false);
                  setFeedbackText("");
                  setInitialFeedbackText("");
                  setHasNewRating(false);
                }
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleFeedbackSubmit}
              disabled={!isInputDirty || isSubmitting}>
              {isSubmitting ? "Submit" : "Submit"}
            </Button>
            {/* <button
              className="feedback-rating-button-submit"
              onClick={handleFeedbackSubmit}
              disabled={!isInputDirty || isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
};
